import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Divider, List, Modal, Row, Space, Tag, Typography, message } from 'antd';
import { ArrowUpOutlined, CheckCircleOutlined, CheckOutlined, CrownOutlined, DollarOutlined, InfoCircleOutlined, RocketOutlined, StarOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { PLANS } from '../../services/planService';
import { formatFeatureName, formatPlanLimit, formatPlanPrice, getPlanLimits, PLAN_LIMITS } from '../../utils/planPresentation';
// Accent contextuel : noir dans l'espace privé, bleu en dehors.
import { PRIVATE_PRIMARY_COLOR as PRIMARY_COLOR } from '../../theme/privateTheme';

const { Title, Text } = Typography;

/**
 * Affiche les offres disponibles et utilise exclusivement les informations
 * fournies par l'API parent, notamment les tarifs et fonctionnalités réelles.
 */
const UpgradeModal = ({ visible, onCancel, currentPlan, plansConfig, planOrder }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);

  /**
   * Réinitialise la sélection à chaque fermeture pour éviter de confirmer par
   * erreur une offre qui avait été choisie lors d'une ouverture précédente.
   */
  useEffect(() => {
    if (!visible) setSelectedPlan(null);
  }, [visible]);

  /**
   * Conserve l'ordre renvoyé par le serveur et bascule sur les clés reçues
   * seulement si l'ordre n'est pas encore disponible.
   */
  const orderedPlans = useMemo(() => {
    const configuredOrder = planOrder?.filter((plan) => plansConfig[plan]) || [];
    return configuredOrder.length ? configuredOrder : Object.keys(plansConfig);
  }, [planOrder, plansConfig]);

  /**
   * Calcule le niveau de l'offre active pour ne proposer que les évolutions
   * autorisées par le serveur, jamais une rétrogradation.
   */
  const currentPlanIndex = useMemo(() => orderedPlans.indexOf(currentPlan), [currentPlan, orderedPlans]);

  /**
   * Retourne l'icône associée à chaque niveau d'offre.
   */
  const getPlanIcon = (plan) => ({
    [PLANS.DECOUVERTE]: <InfoCircleOutlined />,
    [PLANS.STARTER]: <ThunderboltOutlined />,
    [PLANS.ESSENTIEL]: <RocketOutlined />,
    [PLANS.CROISSANCE]: <StarOutlined />,
    [PLANS.EXCELLENCE]: <CrownOutlined />
  }[plan] || <InfoCircleOutlined />);

  /**
   * Détermine si le plan visé est réellement supérieur à l'offre actuelle.
   */
  const isPlanUpgrade = (plan) => orderedPlans.indexOf(plan) > currentPlanIndex;

  /**
   * Enregistre une sélection uniquement quand elle correspond à une montée en
   * gamme possible, ce qui garde l'interface cohérente avec la route API.
   */
  const handlePlanSelect = (plan) => {
    if (isPlanUpgrade(plan)) setSelectedPlan(plan);
  };

  /**
   * Informe l'utilisateur que seule une confirmation de paiement peut activer
   * une offre. Cette interface ne modifie jamais directement l'abonnement.
   */
  const handleUpgrade = () => {
    if (!selectedPlan) return;
    message.info('Le paiement sécurisé sera disponible prochainement. Contactez le support pour modifier votre offre.');
  };

  /**
   * Construit une carte détaillée avec les quotas et toutes les fonctionnalités
   * afin qu'aucun élément ajouté à une offre ne soit masqué dans ce choix.
   */
  const renderPlanCard = (planKey) => {
    const plan = plansConfig[planKey];
    const isCurrent = planKey === currentPlan;
    const isUpgrade = isPlanUpgrade(planKey);
    const isSelected = planKey === selectedPlan;
    const isRecommended = orderedPlans[currentPlanIndex + 1] === planKey;

    return <Card key={planKey} hoverable={isUpgrade} onClick={() => handlePlanSelect(planKey)} className={`h-full relative ${isSelected ? 'border-blue-500 shadow-lg' : ''} ${!isUpgrade && !isCurrent ? 'opacity-60 cursor-not-allowed' : ''}`} style={{ borderColor: isSelected ? PRIMARY_COLOR : undefined, background: isCurrent ? 'var(--private-soft, #f0f8ff)' : undefined }}>
      {isCurrent && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><Tag color="green">Offre actuelle</Tag></div>}
      {isRecommended && <div className="absolute -top-3 right-4"><Tag color="orange">Recommandée</Tag></div>}
      <div className="text-center"><div className="text-3xl mb-2">{getPlanIcon(planKey)}</div><Title level={4} className="!mb-1">{plan.name}</Title><Text type="secondary">{plan.recommended_for}</Text><div className="my-4"><Title level={2} className="!mb-0">{formatPlanPrice(plan.price, plan.currency)}</Title><Text type="secondary">par mois</Text></div><Text className="block min-h-12">{plan.description}</Text><Divider />
        <div className="text-left"><Text strong>Quotas inclus</Text><List size="small" dataSource={PLAN_LIMITS} renderItem={({ key, label }) => <List.Item><Text>{label}</Text><Text strong>{formatPlanLimit(getPlanLimits(plan)[key])}</Text></List.Item>} /></div>
        <div className="text-left mt-4"><Text strong>Fonctionnalités incluses</Text><List size="small" dataSource={plan.features || []} renderItem={(feature) => <List.Item><CheckCircleOutlined className="mr-2 text-green-500" />{formatFeatureName(feature)}</List.Item>} /></div>
        <div className="mt-4">{isCurrent ? <Button disabled block>Offre actuelle</Button> : !isUpgrade ? <Button disabled block>Rétrogradation non disponible</Button> : <Button type={isSelected ? 'primary' : 'default'} block icon={isSelected ? <CheckOutlined /> : <ArrowUpOutlined />}>{isSelected ? 'Offre sélectionnée' : 'Choisir cette offre'}</Button>}</div>
      </div>
    </Card>;
  };

  const selectedPlanConfig = selectedPlan ? plansConfig[selectedPlan] : null;

  return <Modal title={<Space><ArrowUpOutlined /><span>Changer mon offre</span></Space>} open={visible} onCancel={onCancel} width={1200} footer={[<Button key="cancel" onClick={onCancel}>Annuler</Button>, <Button key="upgrade" type="primary" disabled={!selectedPlan} onClick={handleUpgrade} icon={<DollarOutlined />}>Contacter le support</Button>]}>
    <div className="space-y-6"><Alert message="Choisissez une offre adaptée à votre activité" description="Une offre est activée uniquement après confirmation de paiement. Contactez le support pour finaliser votre changement d’offre." type="info" showIcon />
      <Row gutter={[16, 24]}>{orderedPlans.map((planKey) => <Col xs={24} sm={12} lg={6} key={planKey}>{renderPlanCard(planKey)}</Col>)}</Row>
      {selectedPlanConfig && <Card className="bg-blue-50 border-blue-200"><Title level={5}>Récapitulatif</Title><div className="flex flex-wrap items-center justify-between gap-3"><Space><Tag>{plansConfig[currentPlan]?.name || currentPlan}</Tag><ArrowUpOutlined /><Tag color="blue" className="private-tag-accent" >{selectedPlanConfig.name}</Tag></Space><Text strong>{formatPlanPrice(selectedPlanConfig.price, selectedPlanConfig.currency)}/mois</Text></div></Card>}
    </div>
  </Modal>;
};

export default UpgradeModal;
